import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Phone, Minimize2, Maximize2, Navigation, Expand, Shrink, MessageSquare } from "lucide-react";
import { useCall } from "@/context/CallContext";
import { CallChat } from "@/components/site/CallChat";

function VideoEl({ stream, muted, className, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null;
  }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} data-testid={testId} />;
}

const SECTIONS = [
  ["lesson-purpose", "Purpose"],
  ["lesson-flow-block", "60-min flow"],
  ["lesson-class-questions", "Class questions"],
  ["lesson-homework", "Homework"],
  ["lesson-worksheet", "Worksheet"],
  ["lesson-deliverables", "Deliverables"],
];

function IncomingCall({ incoming, accept, hangUp }) {
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] shadow-2xl p-5 no-print" data-testid="incoming-call">
      <p className="font-mono-ui text-[11px] tracking-widest text-[#F08A3C]">INCOMING VIDEO CALL</p>
      <p className="mt-2 font-display text-lg font-semibold text-white">{incoming.host_name || "Mr. Greg"} is calling</p>
      <p className="text-sm text-[#F5ECD7]/70">One-on-one video lesson</p>
      <div className="mt-4 flex gap-2">
        <button onClick={accept} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-white py-2.5 font-semibold" data-testid="call-accept-btn"><Phone className="h-4 w-4" /> Join</button>
        <button onClick={hangUp} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-rose-500 text-white py-2.5 font-semibold" data-testid="call-decline-btn"><PhoneOff className="h-4 w-4" /> Decline</button>
      </div>
    </div>
  );
}

export function CallWindow() {
  const c = useCall();
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [mini, setMini] = useState(false);
  const [full, setFull] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [seen, setSeen] = useState(0);
  if (!c) return null;
  if (c.incoming && !c.call) return <IncomingCall incoming={c.incoming} accept={c.accept} hangUp={c.hangUp} />;
  if (!c.call) return null;

  const toggle = (kind, on, set) => {
    c.localStream?.getTracks().filter((t) => t.kind === kind).forEach((t) => (t.enabled = !on));
    set(!on);
  };
  const other = c.isHost ? c.call.student_name : c.call.host_name || "Mr. Greg";
  const status = c.connState === "connected" ? "Live" : c.connState === "ringing" ? "Ringing..." : c.connState === "failed" ? "Connection failed" : "Connecting...";
  const btn = "h-10 w-10 rounded-full flex items-center justify-center";
  const chatOpen = showChat && !mini;
  const unread = chatOpen ? 0 : c.chat.filter((m) => !m.me).length - seen;
  const size = mini ? "bottom-4 right-4 w-56" : full ? "inset-2 sm:inset-4" : "bottom-4 right-4 w-[min(760px,calc(100vw-2rem))]";

  return (
    <div className={`fixed z-[60] rounded-2xl bg-[#071C33] shadow-2xl ring-1 ring-white/10 overflow-hidden no-print flex flex-col ${size}`} data-testid="call-window">
      <div className="flex items-center justify-between px-3 py-2 text-[#F5ECD7] text-xs">
        <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${c.connState === "connected" ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`} /><span data-testid="call-status">{status}</span> · {other}</span>
        <span className="flex items-center gap-3">
          {!mini && <button onClick={() => setFull(!full)} aria-label="Full screen" data-testid="call-fullscreen-btn">{full ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}</button>}
          <button onClick={() => { setMini(!mini); setFull(false); }} aria-label="Resize" data-testid="call-minimize-btn">{mini ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}</button>
        </span>
      </div>
      <div className={`flex-1 min-h-0 flex ${full ? "flex-col lg:flex-row" : "flex-col"}`}>
      <div className={`relative bg-black ${full ? "flex-1 min-h-0" : "aspect-video"}`}>
        <VideoEl stream={c.remoteStream} className={`w-full h-full -scale-x-100 ${full ? "object-contain" : "object-cover"}`} testId="call-remote-video" />
        {!c.remoteStream && <p className="absolute inset-0 flex items-center justify-center text-xs text-white/60">Waiting for {other}...</p>}
        <VideoEl stream={c.localStream} muted className={`absolute bottom-3 right-3 ${mini ? "w-20" : "w-32 sm:w-44"} aspect-video object-cover rounded-lg ring-2 ring-white/30 bg-slate-800 -scale-x-100`} testId="call-local-video" />
      </div>
      {chatOpen && <div className={full ? "lg:w-80 flex flex-col min-h-0" : ""}><CallChat chat={c.chat} sendChat={c.sendChat} /></div>}
      </div>
      {!mini && c.isHost && c.canShowSection && (
        <div className="px-3 pt-3" data-testid="call-section-jump">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#1A9AA8] mb-1.5 flex items-center gap-1"><Navigation className="h-3 w-3" /> Show the student</p>
          <div className="flex flex-wrap gap-1.5">
            {SECTIONS.map(([id, label]) => (
              <button key={id} onClick={() => c.showSection(id)} className="rounded-full bg-white/10 text-[#F5ECD7] text-[11px] px-2.5 py-1 hover:bg-white/20" data-testid={`call-jump-${id}`}>{label}</button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 p-3">
        {c.isHost ? (
          <label className="flex items-center gap-1.5 text-[11px] text-[#F5ECD7]/80">
            <input type="checkbox" checked={c.follow} onChange={(e) => c.setFollow(e.target.checked)} className="accent-[#1A9AA8]" data-testid="call-follow-toggle" /> Student follows me
          </label>
        ) : <span className="text-[11px] text-[#F5ECD7]/70">Following Mr. Greg</span>}
        <div className="flex gap-2">
          {!mini && <button onClick={() => { setShowChat(!showChat); setSeen(c.chat.filter((m) => !m.me).length); }} className={`${btn} relative ${showChat ? "bg-[#1A9AA8] text-white" : "bg-white/15 text-white"}`} aria-label="Chat" data-testid="call-chat-toggle"><MessageSquare className="h-4 w-4" />{unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[#F08A3C] text-[10px] flex items-center justify-center" data-testid="call-chat-unread">{unread}</span>}</button>}
          <button onClick={() => toggle("audio", mic, setMic)} className={`${btn} ${mic ? "bg-white/15 text-white" : "bg-amber-500 text-white"}`} aria-label="Microphone" data-testid="call-mic-btn">{mic ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</button>
          <button onClick={() => toggle("video", cam, setCam)} className={`${btn} ${cam ? "bg-white/15 text-white" : "bg-amber-500 text-white"}`} aria-label="Camera" data-testid="call-cam-btn">{cam ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}</button>
          <button onClick={c.hangUp} className={`${btn} bg-rose-600 text-white`} aria-label="End call" data-testid="call-end-btn"><PhoneOff className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

export function StartCallButton({ studentId, className = "" }) {
  const c = useCall();
  if (!c?.isHost) return null;
  const inCall = !!c.call;
  return (
    <button onClick={() => (inCall ? null : c.startCall(studentId))} disabled={inCall} className={`inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 ${className}`} data-testid="start-video-call-btn">
      <Video className="h-4 w-4" /> {inCall ? "In a call" : "Video call"}
    </button>
  );
}
